/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Button,
  Divider,
  Grid,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import useFacility from '../../hooks/useFacility';

/**
 * ChamberInventoryReport
 *
 * Lists every stored detail for each compartment (equipment/asset info, dates,
 * location, installed lights, etc.), with a search bar and facility filter.
 * Exports the currently-shown compartments as a PDF.
 */

const fmtDate = (d) => (d ? new Date(d).toISOString().split('T')[0] : '');

const fmtLifeExpectancy = (hours) => {
  if (hours === null || hours === undefined || hours === '') return null;
  const h = Number(hours);
  if (isNaN(h)) return null;
  return `${h.toLocaleString()} hrs`;
};

const describeLight = (light) => {
  const type = light.light_type?.light_type || 'Unknown type';
  const extras = [];
  if (light.location) extras.push(`location: ${light.location}`);
  if (light.install_date) extras.push(`installed: ${fmtDate(light.install_date)}`);
  const life = fmtLifeExpectancy(light.light_type?.life_expectancy);
  if (life) extras.push(`life: ${life}`);
  return extras.length > 0 ? `${type} (${extras.join(', ')})` : type;
};

const DetailField = ({ label, value }) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
      {label}
    </Typography>
    <Typography variant="body2">
      {value === null || value === undefined || value === '' ? '—' : value}
    </Typography>
  </Grid>
);

const ChamberInventoryReport = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();

  const [chambers, setChambers] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);

  // Print / Export dialog state
  const [exportOpen, setExportOpen] = useState(false);
  const [allChambers, setAllChambers] = useState([]);
  const [loadingAllChambers, setLoadingAllChambers] = useState(false);
  const [selectedExportChambers, setSelectedExportChambers] = useState(new Set());
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' | 'csv'
  const [exportError, setExportError] = useState(null);

  useEffect(() => {
    if (!prefLoading && facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
  }, [facilityPreference, prefLoading]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const facilityParam = selectedFacility !== 'all' ? `&facility=${selectedFacility}` : '';
      const [chambersRes, facilitiesRes] = await Promise.all([
        axios.get(`/api/chambers?include_inactive=true${facilityParam}`),
        axios.get('/api/facilities')
      ]);
      setChambers(chambersRes.data);
      setFacilities(facilitiesRes.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching inventory data:', err);
      setError('Failed to fetch data');
      setLoading(false);
    }
  }, [selectedFacility]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredChambers = useMemo(() => {
    let filtered = chambers;

    if (selectedFacility && selectedFacility !== 'all') {
      filtered = filtered.filter(c => {
        const fid = (c.facility?._id || c.facility || '').toString();
        return fid === selectedFacility.toString();
      });
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(c =>
        c.chamber?.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q) ||
        c.facility?.facility_name?.toLowerCase().includes(q) ||
        c.model_number?.toLowerCase().includes(q) ||
        c.serial_number?.toLowerCase().includes(q) ||
        c.asset_management_number?.toLowerCase().includes(q) ||
        c.location?.toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [chambers, selectedFacility, searchQuery]);

  // ── Print / Export ────────────────────────────────────────────────
  // Preselect whatever is currently shown on screen, then let the admin
  // refine the facility / individual compartments in the dialog.
  const openExportDialog = async () => {
    setExportError(null);
    setSelectedExportChambers(new Set(filteredChambers.map(c => c._id)));
    setExportOpen(true);
    if (allChambers.length === 0) {
      try {
        setLoadingAllChambers(true);
        const res = await axios.get('/api/chambers?include_inactive=true');
        setAllChambers(res.data);
      } catch (err) {
        console.error('Error loading compartments for export:', err);
        setExportError('Failed to load compartments.');
      } finally {
        setLoadingAllChambers(false);
      }
    }
  };

  const chambersByFacility = useMemo(() => {
    const map = new Map();
    for (const c of allChambers) {
      const fid = (c.facility?._id || c.facility || 'unassigned').toString();
      const fname = c.facility?.facility_name || 'Unassigned Facility';
      if (!map.has(fid)) map.set(fid, { id: fid, name: fname, chambers: [] });
      map.get(fid).chambers.push(c);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [allChambers]);

  const toggleExportChamber = (chamberId) => {
    setSelectedExportChambers(prev => {
      const next = new Set(prev);
      if (next.has(chamberId)) next.delete(chamberId);
      else next.add(chamberId);
      return next;
    });
  };

  const toggleExportFacility = (facilityGroup) => {
    const ids = facilityGroup.chambers.map(c => c._id);
    const allSelected = ids.every(id => selectedExportChambers.has(id));
    setSelectedExportChambers(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else ids.forEach(id => next.add(id));
      return next;
    });
  };

  const allExportSelected = allChambers.length > 0 &&
    allChambers.every(c => selectedExportChambers.has(c._id));

  const toggleSelectAllExport = () => {
    setSelectedExportChambers(() => {
      if (allExportSelected) return new Set();
      return new Set(allChambers.map(c => c._id));
    });
  };

  const handleExportPDF = async () => {
    setExportError(null);
    try {
      setExporting(true);
      const params = new URLSearchParams();
      params.append('format', exportFormat);
      const ids = Array.from(selectedExportChambers);
      // No explicit selection -> include all compartments.
      if (ids.length > 0) params.append('chambers', ids.join(','));

      const res = await axios.get(
        `/api/admin/reports/compartment-inventory-export?${params.toString()}`,
        { responseType: 'blob' }
      );

      const mime = exportFormat === 'csv' ? 'text/csv;charset=utf-8;' : 'application/pdf';
      const ext = exportFormat === 'csv' ? 'csv' : 'pdf';
      const blob = new Blob([res.data], { type: mime });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `compartment-inventory-report-${new Date().toISOString().split('T')[0]}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setExportOpen(false);
    } catch (err) {
      console.error('Error exporting inventory report:', err);
      setExportError('Failed to generate the report. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading compartment inventory...</Typography>
        <LinearProgress sx={{ mt: 2 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search compartments..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ width: 300 }}
          size="small"
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
          }}
        />

        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Filter by Facility</InputLabel>
          <Select
            value={selectedFacility}
            onChange={(e) => setSelectedFacility(e.target.value)}
            label="Filter by Facility"
          >
            <MenuItem value="all">All Facilities</MenuItem>
            {facilities.map(facility => (
              <MenuItem key={facility._id} value={facility._id}>
                {facility.facility_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <Button
          variant="contained"
          size="small"
          startIcon={<PrintIcon />}
          onClick={openExportDialog}
        >
          Print / Export
        </Button>
      </Box>

      {/* Compartment detail cards */}
      {filteredChambers.length === 0 ? (
        <Paper sx={{ p: 3 }}>
          <Typography color="textSecondary" align="center">No compartments found</Typography>
        </Paper>
      ) : (
        filteredChambers.map(chamber => {
          const unit = chamber.capacity?.unit;
          const capacityText =
            unit && chamber.capacity?.max_capacity !== undefined
              ? `${chamber.capacity.max_capacity} ${unit.base_unit}`
              : (chamber.capacity?.max_capacity ?? '');
          const lights = chamber.lights || [];

          return (
            <Paper key={chamber._id} sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {chamber.chamber}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {chamber.description}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Chip
                  label={chamber.status}
                  size="small"
                  color={chamber.status === 'Active' ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
              <Divider sx={{ mb: 1.5 }} />

              <Grid container spacing={2}>
                <DetailField label="Facility" value={chamber.facility?.facility_name} />
                <DetailField label="Capacity" value={capacityText} />
                <DetailField label="Unit" value={unit?.base_unit} />
                <DetailField label="Model Number" value={chamber.model_number} />
                <DetailField label="Serial Number" value={chamber.serial_number} />
                <DetailField label="Asset Mgmt #" value={chamber.asset_management_number} />
                <DetailField label="Activation Date" value={fmtDate(chamber.activation_date)} />
                <DetailField label="Deactivation Date" value={fmtDate(chamber.deactivation_date)} />
                <DetailField label="Location" value={chamber.location} />
              </Grid>

              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                  Lights
                </Typography>
                {lights.length === 0 ? (
                  <Typography variant="body2">None</Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                    {lights.map((light, idx) => (
                      <Typography key={idx} variant="body2">• {describeLight(light)}</Typography>
                    ))}
                  </Box>
                )}
              </Box>

              {(chamber.notes || chamber.comments) && (
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {chamber.notes && <DetailField label="Notes" value={chamber.notes} />}
                  {chamber.comments && <DetailField label="Comments" value={chamber.comments} />}
                </Grid>
              )}
            </Paper>
          );
        })
      )}

      <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
        Total: {filteredChambers.length} compartments
      </Typography>

      {/* Print PDF dialog */}
      <Dialog open={exportOpen} onClose={() => !exporting && setExportOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Print Compartment Inventory Report</DialogTitle>
        <DialogContent dividers>
          {exportError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
              {exportError}
            </Alert>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <FormLabel>Compartments to include</FormLabel>
            {allChambers.length > 0 && (
              <FormControlLabel
                control={<Checkbox checked={allExportSelected} onChange={toggleSelectAllExport} size="small" />}
                label="Select all"
              />
            )}
          </Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            Pick individual compartments, or use a facility checkbox to include all of its compartments. Leaving everything unchecked includes every compartment.
          </Typography>

          {loadingAllChambers ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading compartments…</Typography>
            </Box>
          ) : (
            <Box sx={{ maxHeight: 340, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1, p: 1 }}>
              {chambersByFacility.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>
                  No compartments available.
                </Typography>
              ) : (
                chambersByFacility.map(group => {
                  const groupIds = group.chambers.map(c => c._id);
                  const allChecked = groupIds.every(id => selectedExportChambers.has(id));
                  const someChecked = !allChecked && groupIds.some(id => selectedExportChambers.has(id));
                  return (
                    <Box key={group.id} sx={{ mb: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={() => toggleExportFacility(group)}
                            size="small"
                          />
                        }
                        label={<Typography fontWeight="bold">{group.name}</Typography>}
                      />
                      <Box sx={{ pl: 4, display: 'flex', flexDirection: 'column' }}>
                        {group.chambers.map(c => (
                          <FormControlLabel
                            key={c._id}
                            control={
                              <Checkbox
                                checked={selectedExportChambers.has(c._id)}
                                onChange={() => toggleExportChamber(c._id)}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2">
                                {c.chamber}
                                {c.description ? ` — ${c.description}` : ''}
                                {c.status && c.status !== 'Active' ? ` (${c.status})` : ''}
                              </Typography>
                            }
                          />
                        ))}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <FormControl>
            <FormLabel>File format</FormLabel>
            <RadioGroup
              row
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value)}
            >
              <FormControlLabel value="pdf" control={<Radio />} label="PDF" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportOpen(false)} disabled={exporting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleExportPDF}
            disabled={exporting}
            startIcon={exporting ? <CircularProgress size={16} color="inherit" /> : <PrintIcon />}
          >
            {exporting ? 'Generating…' : `Download ${exportFormat.toUpperCase()}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChamberInventoryReport;
